import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Wind, Mountain, Droplets, Flame, Info, Lightbulb } from 'lucide-react';
import { THEMES } from '../../utils/constants';

export const MasterTipsCard = ({ title, subtitle, children, element = 'air' }) => {
    const theme = THEMES[element] || THEMES['air'];
    const storageKey = `sf_master_tip_${element}`;

    // Default to collapsed (false) unless user has explicitly expanded it previously
    const [isExpanded, setIsExpanded] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        return saved === 'true';
    });

    const toggle = () => {
        const newState = !isExpanded;
        setIsExpanded(newState);
        localStorage.setItem(storageKey, newState.toString());
    };

    // Icon Mapping
    const Icon = {
        air: Wind,
        earth: Mountain,
        water: Droplets,
        fire: Flame
    }[element] || Info;

    // Dynamic Styles based on state
    const containerClasses = isExpanded 
        ? `bg-slate-900 border-${theme.tailwind}/30 shadow-lg` 
        : `bg-transparent border-transparent hover:bg-slate-900/30 hover:border-slate-800`;

    return (
        <div className={`mb-8 border rounded-2xl transition-all duration-300 animate-fade-in overflow-hidden ${containerClasses}`}>
            
            {/* --- HEADER ROW (Always Visible) --- */}
            <div 
                onClick={toggle}
                className="p-4 flex items-center justify-between cursor-pointer group select-none"
            >
                {/* Left: Icon & Titles */}
                <div className="flex items-center gap-4 flex-1">
                    <div className={`p-2.5 rounded-xl transition-colors ${isExpanded ? `${theme.bg} text-${theme.tailwind}` : 'bg-slate-900 text-slate-500 group-hover:text-slate-300 border border-slate-800'}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                    
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold uppercase tracking-widest transition-colors ${isExpanded ? `text-${theme.tailwind}` : 'text-slate-500 group-hover:text-slate-400'}`}>
                                {element}
                            </span>
                            {!isExpanded && (
                                <span className="text-[10px] bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-700 opacity-0 group-hover:opacity-100 transition-opacity">
                                    Click to Expand
                                </span>
                            )}
                        </div>
                        <h2 className={`text-lg font-bold transition-colors ${isExpanded ? 'text-white' : 'text-slate-300 group-hover:text-white'}`}>
                            {title}
                        </h2>
                        {/* Subtitle: Only show in collapsed state on desktop to save space, or always show if preferred. 
                            Let's hide it in expanded state to focus on the content, or keep it as context. 
                            Design Choice: Keep it always, but fade it. */}
                        <p className={`text-xs font-medium transition-colors hidden sm:block ${isExpanded ? 'text-slate-400' : 'text-slate-600 group-hover:text-slate-500'}`}>
                            {subtitle}
                        </p>
                    </div>
                </div>

                {/* Right: Toggle Chevron */}
                <div className={`p-2 rounded-full transition-all ${isExpanded ? 'bg-slate-800 text-white' : 'text-slate-600 group-hover:text-white group-hover:bg-slate-800'}`}>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
            </div>

            {/* --- EXPANDED CONTENT (The "Tip") --- */}
            {isExpanded && (
                <div className="px-4 pb-4 sm:px-16 sm:pb-6 animate-slide-down">
                    {/* Divider */}
                    <div className={`h-px w-full ${theme.bg} opacity-20 mb-4`}></div>
                    
                    <div className="flex gap-4">
                        <Lightbulb className={`w-5 h-5 shrink-0 text-${theme.tailwind} opacity-80 mt-0.5`} />
                        <div className="text-sm text-slate-300 leading-relaxed space-y-2">
                            {children}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};